import { useCallback, useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, formatApiError, formatDate, formatDateTime, REFERRAL_CATEGORIES } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Plus, Share2, X, CheckCircle2, Pencil, Inbox, ArrowUpRight, ArrowDownLeft } from "lucide-react";
import MetadataFooter from "@/components/MetadataFooter";

const STATUSES = [
  { value: "pending", label: "Pending" },
  { value: "in_progress", label: "In Progress" },
  { value: "completed", label: "Completed" },
  { value: "not_completed", label: "Not Completed" },
  { value: "declined", label: "Declined" },
];

// Phase 25 — outcomes captured when marking a referral complete
const OUTCOME_OPTIONS = [
  { value: "need_met",          label: "Need met",          desc: "The Care Seeker got what they were referred for." },
  { value: "partial",           label: "Partial",           desc: "Some help received but the need is not fully met." },
  { value: "unable_to_connect", label: "Unable to connect", desc: "Care Seeker couldn't reach / be reached by the partner." },
  { value: "declined",          label: "Declined",          desc: "Care Seeker declined the service / referral." },
  { value: "lost_contact",      label: "Lost contact",      desc: "Lost contact with the Care Seeker." },
  { value: "other",             label: "Other",             desc: "Doesn't fit the above categories." },
];
const OUTCOME_LABEL = Object.fromEntries(OUTCOME_OPTIONS.map((o) => [o.value, o.label]));

const INBOUND_STATUSES = [
  { value: "pending", label: "Pending review" },
  { value: "accepted", label: "Accepted" },
  { value: "rejected", label: "Rejected" },
  { value: "all", label: "All" },
];

const URGENCY_STYLE = {
  critical: "bg-[#FEE2E2] text-[#991B1B]",
  high: "bg-[#FEF3C7] text-[#92400E]",
  medium: "bg-[#DBEAFE] text-[#1E40AF]",
  low: "bg-[#F1F5F9] text-[#64748B]",
};

export default function Referrals() {
  const { can } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const csId = searchParams.get("care_seeker_id");
  const initialDir = searchParams.get("dir") === "inbound" ? "inbound" : "outbound";
  const [direction, setDirection] = useState(initialDir);
  const [referrals, setReferrals] = useState([]);
  const [inbound, setInbound] = useState([]);
  const [inboundStatus, setInboundStatus] = useState("pending");
  const [careSeekers, setCareSeekers] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [includeArchived, setIncludeArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(!!csId || searchParams.get("new") === "1");
  const [editing, setEditing] = useState(null);

  const setDir = (d) => {
    setDirection(d);
    const next = new URLSearchParams(searchParams);
    if (d === "inbound") next.set("dir", "inbound");
    else next.delete("dir");
    setSearchParams(next, { replace: true });
  };

  const loadOutbound = useCallback(async () => {
    const params = {};
    if (statusFilter) params.status = statusFilter;
    if (categoryFilter) params.category = categoryFilter;
    if (csId) params.care_seeker_id = csId;
    if (includeArchived) params.include_archived = true;
    const [r, cs] = await Promise.all([
      api.get("/referrals", { params }),
      careSeekers.length === 0 ? api.get("/care-seekers") : Promise.resolve({ data: careSeekers }),
    ]);
    setReferrals(r.data);
    if (cs.data) setCareSeekers(cs.data);
  }, [statusFilter, categoryFilter, csId, includeArchived, careSeekers]);

  const loadInbound = useCallback(async () => {
    const { data } = await api.get("/intake-requests", {
      params: { status: inboundStatus },
    });
    setInbound(data || []);
  }, [inboundStatus]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (direction === "inbound") {
        await loadInbound();
      } else {
        await loadOutbound();
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [direction, loadInbound, loadOutbound]);
  useEffect(() => { load(); }, [load]);

  // Phase 25 — referral outcome dialog
  const [completingRef, setCompletingRef] = useState(null);

  const csName = (id) => {
    const cs = careSeekers.find((c) => c.id === id);
    return cs ? `${cs.preferred_name || cs.first_name} ${cs.last_name}` : "Unknown";
  };

  const updateStatus = async (id, newStatus) => {
    // For "completed", open the outcome dialog instead of patching directly.
    if (newStatus === "completed") {
      const r = referrals.find((x) => x.id === id);
      if (r) { setCompletingRef(r); return; }
    }
    try {
      await api.patch(`/referrals/${id}`, { status: newStatus });
      toast.success("Referral updated");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const submitCompletion = async (refId, outcome, notes) => {
    try {
      await api.patch(`/referrals/${refId}`, {
        status: "completed",
        outcome,
        outcome_notes: notes || null,
        completed_date: new Date().toISOString().slice(0, 10),
      });
      toast.success("Referral marked complete");
      setCompletingRef(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const acceptInbound = async (req) => {
    try {
      const { data } = await api.post(`/intake-requests/${req.id}/accept`, { decision_notes: null });
      toast.success(`Care Seeker ${data.display_id} created`);
      loadInbound();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const rejectInbound = async (req) => {
    const reason = window.prompt(`Reject the referral for ${req.seeker_first_name} ${req.seeker_last_name}?\n\nOptional reason (visible in audit log only):`);
    if (reason === null) return;
    try {
      await api.post(`/intake-requests/${req.id}/reject`, { decision_notes: reason.trim() || null });
      toast.success("Referral rejected");
      loadInbound();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const statusColor = (s) => ({
    pending: "bg-[#FEF3C7] text-[#92400E]",
    in_progress: "bg-[#DBEAFE] text-[#1E40AF]",
    completed: "bg-[#E8ECE7] text-[#166534]",
    not_completed: "bg-[#FEE2E2] text-[#991B1B]",
    declined: "bg-[#F1F5F9] text-[#64748B]",
    accepted: "bg-[#E8ECE7] text-[#166534]",
    rejected: "bg-[#FEE2E2] text-[#991B1B]",
  }[s] || "bg-[#F1F5F9] text-[#64748B]");

  return (
    <div className="space-y-6" data-testid="referrals-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="hw-eyebrow mb-2">Care navigation</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold text-[#0F172A]">Referrals</h1>
          <p className="text-sm text-[#334155] mt-1">
            {direction === "inbound"
              ? "Partner-submitted referrals coming in to HarborWell from the public /refer form."
              : "Referrals HarborWell has made out to other agencies on behalf of a Care Seeker."}
          </p>
        </div>
        {direction === "outbound" && can("referrals.create") && (
          <button onClick={() => setShowForm(true)} data-testid="add-referral-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1E3A8A] text-white text-sm font-medium hover:bg-[#172554]">
            <Plus className="w-4 h-4" /> New referral
          </button>
        )}
      </div>

      {/* Direction tabs */}
      <div className="inline-flex bg-white border border-[#E5E5E0] rounded-full p-1" data-testid="referrals-direction-tabs">
        <button
          data-testid="tab-outbound"
          onClick={() => setDir("outbound")}
          className={`px-4 py-2 rounded-full text-sm font-medium inline-flex items-center gap-2 transition-colors ${
            direction === "outbound" ? "bg-[#1E3A8A] text-white" : "text-[#334155] hover:bg-[#F9F8F6]"
          }`}
        >
          <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
          Outbound
          <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${direction === "outbound" ? "bg-white/20" : "bg-[#F1F5F9]"}`}>
            {direction === "outbound" ? referrals.length : ""}
          </span>
        </button>
        <button
          data-testid="tab-inbound"
          onClick={() => setDir("inbound")}
          className={`px-4 py-2 rounded-full text-sm font-medium inline-flex items-center gap-2 transition-colors ${
            direction === "inbound" ? "bg-[#1E3A8A] text-white" : "text-[#334155] hover:bg-[#F9F8F6]"
          }`}
        >
          <ArrowDownLeft className="w-4 h-4" strokeWidth={1.75} />
          Inbound
          <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${direction === "inbound" ? "bg-white/20" : "bg-[#F1F5F9]"}`}>
            {direction === "inbound" ? inbound.length : ""}
          </span>
        </button>
      </div>

      {direction === "outbound" ? (
        <>
          <div className="bg-white border border-[#E5E5E0] rounded-2xl p-4 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-3 py-2 rounded-xl bg-[#F9F8F6] border border-[#E5E5E0] text-sm flex-1" data-testid="referrals-status-filter">
              <option value="">All statuses</option>
              {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
            <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className="px-3 py-2 rounded-xl bg-[#F9F8F6] border border-[#E5E5E0] text-sm flex-1" data-testid="referrals-category-filter">
              <option value="">All categories</option>
              {REFERRAL_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
            <label className="flex items-center gap-2 text-sm whitespace-nowrap" title="By default, referrals completed more than 30 days ago are hidden">
              <input
                type="checkbox"
                checked={includeArchived}
                onChange={(e) => setIncludeArchived(e.target.checked)}
                data-testid="referrals-archived-filter"
              />
              Show completed &gt; 30d
            </label>
          </div>

          {loading ? (
            <div className="text-center py-12 text-[#1E3A8A]">Loading…</div>
          ) : referrals.length === 0 ? (
            <div className="bg-white border border-[#E5E5E0] rounded-2xl p-12 text-center" data-testid="outbound-empty">
              <Share2 className="w-10 h-10 text-[#1E3A8A] mx-auto mb-3" strokeWidth={1.5} />
              <h3 className="font-display text-lg font-semibold text-[#0F172A]">No outbound referrals yet</h3>
            </div>
          ) : (
            <ul className="space-y-3" data-testid="referrals-list">
              {referrals.map((r) => (
                <li key={r.id} className="bg-white border border-[#E5E5E0] rounded-2xl p-5">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className="font-display font-semibold text-[#0F172A]">{r.organization}</span>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wider ${statusColor(r.status)}`}>{r.status.replace(/_/g, " ")}</span>
                        <span className="text-[10px] bg-[#E8ECE7] text-[#1E3A8A] px-2 py-0.5 rounded-full capitalize">{r.category.replace(/_/g, " ")}</span>
                        <span className="text-[10px] bg-[#DBEAFE] text-[#1E40AF] px-2 py-0.5 rounded-full uppercase tracking-wider font-semibold inline-flex items-center gap-1">
                          <ArrowUpRight className="w-3 h-3" strokeWidth={2} /> Outbound
                        </span>
                      </div>
                      <div className="text-xs text-[#1E3A8A]">For {csName(r.care_seeker_id)} · referred {formatDate(r.referral_date)}</div>
                      {r.outcome && (
                        <div
                          data-testid={`referral-outcome-${r.id}`}
                          className="inline-flex items-center gap-1.5 mt-1.5 px-2.5 py-1 rounded-full bg-[#F0FDF4] border border-[#BBF7D0] text-[11px] text-[#166534]"
                          title={r.outcome_notes || ""}
                        >
                          Outcome: {OUTCOME_LABEL[r.outcome] || r.outcome}
                        </div>
                      )}
                      {r.notes && <p className="text-sm text-[#334155] mt-2">{r.notes}</p>}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {r.status !== "completed" && (
                        <button onClick={() => updateStatus(r.id, "completed")} className="px-3 py-1.5 rounded-full bg-[#E8ECE7] text-[#166534] text-xs font-medium inline-flex items-center gap-1" data-testid={`mark-complete-${r.id}`}>
                          <CheckCircle2 className="w-3.5 h-3.5" /> Mark complete
                        </button>
                      )}
                      <select value={r.status} onChange={(e) => updateStatus(r.id, e.target.value)} className="px-3 py-1.5 rounded-full border border-[#E5E5E0] text-xs">
                        {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                      </select>
                      {can("referrals.edit") && (
                        <button
                          onClick={() => setEditing(r)}
                          data-testid={`edit-referral-${r.id}`}
                          className="text-xs text-[#1E3A8A] inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#F9F8F6] border border-[#E5E5E0] hover:bg-white"
                        >
                          <Pencil className="w-3.5 h-3.5" /> Edit
                        </button>
                      )}
                    </div>
                  </div>
                  <MetadataFooter entity={r} className="mt-3 pt-2 border-t border-[#F0EFEB]" />
                </li>
              ))}
            </ul>
          )}
        </>
      ) : (
        <>
          <div className="bg-white border border-[#E5E5E0] rounded-2xl p-4">
            <select
              value={inboundStatus}
              onChange={(e) => setInboundStatus(e.target.value)}
              className="px-3 py-2 rounded-xl bg-[#F9F8F6] border border-[#E5E5E0] text-sm w-full sm:w-auto"
              data-testid="inbound-status-filter"
            >
              {INBOUND_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>

          {loading ? (
            <div className="text-center py-12 text-[#1E3A8A]">Loading…</div>
          ) : inbound.length === 0 ? (
            <div className="bg-white border border-[#E5E5E0] rounded-2xl p-12 text-center" data-testid="inbound-empty">
              <Inbox className="w-10 h-10 text-[#1E3A8A] mx-auto mb-3" strokeWidth={1.5} />
              <h3 className="font-display text-lg font-semibold text-[#0F172A]">
                {inboundStatus === "pending"
                  ? "No pending inbound referrals"
                  : "No inbound referrals to show"}
              </h3>
              <p className="text-sm text-[#334155] mt-2 max-w-md mx-auto">
                Partner agencies and community members can submit a Care Seeker referral
                from the public form at <span className="font-mono text-[#1E3A8A]">/refer</span>.
              </p>
            </div>
          ) : (
            <ul className="space-y-3" data-testid="inbound-list">
              {inbound.map((req) => (
                <li
                  key={req.id}
                  data-testid={`inbound-${req.id}`}
                  className="bg-white border border-[#E5E5E0] rounded-2xl p-5"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className="font-display font-semibold text-[#0F172A]">
                          {req.seeker_preferred_name || req.seeker_first_name} {req.seeker_last_name}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wider ${statusColor(req.status)}`}>
                          {req.status}
                        </span>
                        {req.urgency && (
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wider ${URGENCY_STYLE[req.urgency] || URGENCY_STYLE.medium}`}>
                            {req.urgency}
                          </span>
                        )}
                        <span className="text-[10px] bg-[#E0E7FF] text-[#1E3A8A] px-2 py-0.5 rounded-full uppercase tracking-wider font-semibold inline-flex items-center gap-1">
                          <ArrowDownLeft className="w-3 h-3" strokeWidth={2} /> Inbound
                        </span>
                      </div>
                      <div className="text-xs text-[#1E3A8A]">
                        From {req.referrer_name}
                        {req.referrer_organization ? ` · ${req.referrer_organization}` : ""}
                        {" · "}
                        received {formatDateTime(req.created_at)}
                      </div>
                      <div className="text-xs text-[#334155] mt-1 space-x-2">
                        {req.seeker_phone && <span>📞 {req.seeker_phone}</span>}
                        {req.seeker_county && <span>📍 {req.seeker_county} County</span>}
                      </div>
                      {req.primary_need && (
                        <p className="text-sm text-[#0F172A] mt-2">
                          <strong>Need:</strong> {req.primary_need}
                        </p>
                      )}
                      {req.notes && <p className="text-sm text-[#334155] mt-1 whitespace-pre-wrap">{req.notes}</p>}
                      {req.status === "accepted" && req.resulting_care_seeker_id && (
                        <Link
                          to={`/care-seekers/${req.resulting_care_seeker_id}`}
                          className="inline-flex items-center gap-1 mt-2 text-xs text-[#1E3A8A] font-medium hover:underline"
                          data-testid={`view-cs-${req.id}`}
                        >
                          View created Care Seeker <ArrowUpRight className="w-3 h-3" />
                        </Link>
                      )}
                      {req.status === "rejected" && req.decision_notes && (
                        <p className="text-xs text-[#991B1B] mt-2 italic">Reason: {req.decision_notes}</p>
                      )}
                    </div>
                    {req.status === "pending" && (
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => acceptInbound(req)}
                          data-testid={`accept-${req.id}`}
                          className="px-3 py-1.5 rounded-full bg-[#1E3A8A] text-white text-xs font-medium inline-flex items-center gap-1 hover:bg-[#172554]"
                        >
                          <CheckCircle2 className="w-3.5 h-3.5" /> Accept → Create CSK
                        </button>
                        <button
                          onClick={() => rejectInbound(req)}
                          data-testid={`reject-${req.id}`}
                          className="px-3 py-1.5 rounded-full bg-white border border-[#E5E5E0] text-xs text-[#334155] font-medium hover:bg-[#F9F8F6]"
                        >
                          Reject
                        </button>
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {showForm && (
        <ReferralForm
          careSeekers={careSeekers}
          defaultCsId={csId}
          onClose={() => setShowForm(false)}
          onCreated={() => { setShowForm(false); load(); }}
        />
      )}
      {editing && (
        <ReferralForm
          careSeekers={careSeekers}
          existing={editing}
          onClose={() => setEditing(null)}
          onCreated={() => { setEditing(null); load(); }}
        />
      )}
      {completingRef && (
        <MarkCompleteDialog
          referral={completingRef}
          onClose={() => setCompletingRef(null)}
          onConfirm={(outcome, notes) => submitCompletion(completingRef.id, outcome, notes)}
        />
      )}
    </div>
  );
}

function MarkCompleteDialog({ referral, onClose, onConfirm }) {
  const [outcome, setOutcome] = useState("need_met");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await onConfirm(outcome, notes.trim());
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#0F172A]/40" onClick={onClose}>
      <form
        onSubmit={submit}
        className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="mark-complete-dialog"
      >
        <div className="px-5 py-4 border-b border-[#E5E5E0]">
          <div className="hw-eyebrow text-[10px] mb-1">Closing the loop</div>
          <h2 className="font-display text-lg font-semibold text-[#0F172A]">
            Mark referral complete
          </h2>
          <p className="text-sm text-[#334155] mt-1">
            <span className="font-medium">{referral.organization}</span> ·
            {" "}{referral.category}
          </p>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs uppercase tracking-wider font-semibold text-[#1E3A8A] mb-2">
              What was the outcome?
            </label>
            <div className="space-y-2">
              {OUTCOME_OPTIONS.map((o) => (
                <label
                  key={o.value}
                  className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                    outcome === o.value
                      ? "border-[#1E3A8A] bg-[#E0E7FF]"
                      : "border-[#E5E5E0] hover:bg-[#F9F8F6]"
                  }`}
                  data-testid={`outcome-opt-${o.value}`}
                >
                  <input
                    type="radio"
                    name="outcome"
                    value={o.value}
                    checked={outcome === o.value}
                    onChange={() => setOutcome(o.value)}
                    className="mt-1 w-4 h-4 text-[#1E3A8A] focus:ring-[#7E937D]"
                  />
                  <div>
                    <div className="text-sm font-medium text-[#0F172A]">{o.label}</div>
                    <div className="text-xs text-[#334155]">{o.desc}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-[#334155] mb-1.5">
              Outcome notes (optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              data-testid="outcome-notes-input"
              className="w-full px-4 py-2 rounded-xl border border-[#E5E5E0] text-sm"
              placeholder="Anything the team should know — appointment date, follow-up needed, etc."
            />
          </div>
        </div>
        <div className="px-5 py-4 border-t border-[#E5E5E0] flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            data-testid="mark-complete-cancel"
            className="px-4 py-2 rounded-full border border-[#E5E5E0] text-sm text-[#334155]"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            data-testid="mark-complete-confirm"
            className="px-4 py-2 rounded-full bg-[#1E3A8A] text-white text-sm font-medium disabled:opacity-60 inline-flex items-center gap-1.5"
          >
            <CheckCircle2 className="w-3.5 h-3.5" />
            {submitting ? "Saving…" : "Mark complete"}
          </button>
        </div>
      </form>
    </div>
  );
}

function ReferralForm({ careSeekers, defaultCsId, onClose, onCreated, existing }) {
  const [form, setForm] = useState({
    care_seeker_id: existing?.care_seeker_id || defaultCsId || "",
    category: existing?.category || "housing",
    organization: existing?.organization || "",
    contact_info: existing?.contact_info || "",
    referral_date: existing?.referral_date || new Date().toISOString().slice(0, 10),
    status: existing?.status || "pending",
    notes: existing?.notes || "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [resources, setResources] = useState([]);
  const [showNewOrg, setShowNewOrg] = useState(false);
  const [newOrg, setNewOrg] = useState({
    name: "",
    phone: "",
    email: "",
    website: "",
    address: "",
  });
  const isEdit = !!existing;

  useEffect(() => {
    api.get("/resources").then(({ data }) => setResources(data || [])).catch(() => {});
  }, []);

  // Stitch the resource fields we have into the referral's free-text
  // contact_info so we don't lose info if the resource is later edited.
  const composeContactInfo = (r) =>
    [r.phone, r.email, r.website, r.address].filter(Boolean).join(" · ");

  const pickResource = (r) => {
    setForm((prev) => ({
      ...prev,
      organization: r.name,
      contact_info: composeContactInfo(r),
    }));
    setShowNewOrg(false);
  };

  const submitNewOrg = async () => {
    if (!newOrg.name.trim()) {
      toast.error("Organization name is required");
      return;
    }
    try {
      const { data } = await api.post("/resources", {
        name: newOrg.name.trim(),
        category: form.category,
        phone: newOrg.phone.trim() || null,
        email: newOrg.email.trim() || null,
        website: newOrg.website.trim() || null,
        address: newOrg.address.trim() || null,
      });
      setResources((prev) => [data, ...prev]);
      pickResource(data);
      toast.success(`Added "${data.name}" to Resources`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.care_seeker_id) return toast.error("Select a Care Seeker");
    setSubmitting(true);
    try {
      if (isEdit) {
        const payload = { ...form };
        delete payload.care_seeker_id;
        delete payload.referral_date;
        await api.patch(`/referrals/${existing.id}`, payload);
        toast.success("Referral updated");
      } else {
        await api.post("/referrals", form);
        toast.success("Referral created");
      }
      onCreated();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#0F172A]/40">
      <div className="bg-white rounded-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-[#E5E5E0] px-6 py-4 flex justify-between">
          <h2 className="font-display text-xl font-semibold">{isEdit ? "Edit" : "New"} Referral</h2>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4" data-testid="referral-form">
          <div>
            <label className="block text-sm font-medium text-[#334155] mb-2">Care Seeker</label>
            <select disabled={isEdit} value={form.care_seeker_id} onChange={(e) => setForm({ ...form, care_seeker_id: e.target.value })} className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0] disabled:bg-[#F9F8F6]" data-testid="ref-cs-select">
              <option value="">Select…</option>
              {careSeekers.map((cs) => <option key={cs.id} value={cs.id}>{cs.preferred_name || cs.first_name} {cs.last_name}</option>)}
            </select>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#334155] mb-2">Category</label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0]" data-testid="ref-category">
                {REFERRAL_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#334155] mb-2">Referral date</label>
              <input type="date" value={form.referral_date} onChange={(e) => setForm({ ...form, referral_date: e.target.value })} className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0]" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-[#334155] mb-2">Organization</label>
            <ResourcePicker
              resources={resources}
              category={form.category}
              value={form.organization}
              onPick={pickResource}
              onType={(v) => setForm({ ...form, organization: v })}
              onAddNew={() => { setShowNewOrg(true); setNewOrg({ name: form.organization, phone: "", email: "", website: "", address: "" }); }}
            />
          </div>
          {showNewOrg && (
            <div className="bg-[#F9F8F6] border border-[#E5E5E0] rounded-2xl p-4 space-y-3" data-testid="ref-new-org-panel">
              <div className="flex items-center justify-between">
                <h3 className="font-display text-sm font-semibold text-[#0F172A]">Add new organization to Resources</h3>
                <button type="button" onClick={() => setShowNewOrg(false)} className="text-[#1E3A8A] text-xs" data-testid="ref-new-org-cancel">Cancel</button>
              </div>
              <input
                value={newOrg.name}
                onChange={(e) => setNewOrg({ ...newOrg, name: e.target.value })}
                placeholder="Organization name *"
                className="w-full px-3 py-2 rounded-xl bg-white border border-[#E5E5E0] text-sm"
                data-testid="ref-new-org-name"
              />
              <div className="grid sm:grid-cols-2 gap-3">
                <input
                  value={newOrg.phone}
                  onChange={(e) => setNewOrg({ ...newOrg, phone: e.target.value })}
                  placeholder="Phone"
                  className="w-full px-3 py-2 rounded-xl bg-white border border-[#E5E5E0] text-sm"
                  data-testid="ref-new-org-phone"
                />
                <input
                  value={newOrg.email}
                  onChange={(e) => setNewOrg({ ...newOrg, email: e.target.value })}
                  placeholder="Email"
                  className="w-full px-3 py-2 rounded-xl bg-white border border-[#E5E5E0] text-sm"
                  data-testid="ref-new-org-email"
                />
              </div>
              <input
                value={newOrg.website}
                onChange={(e) => setNewOrg({ ...newOrg, website: e.target.value })}
                placeholder="Website"
                className="w-full px-3 py-2 rounded-xl bg-white border border-[#E5E5E0] text-sm"
                data-testid="ref-new-org-website"
              />
              <input
                value={newOrg.address}
                onChange={(e) => setNewOrg({ ...newOrg, address: e.target.value })}
                placeholder="Address"
                className="w-full px-3 py-2 rounded-xl bg-white border border-[#E5E5E0] text-sm"
                data-testid="ref-new-org-address"
              />
              <button
                type="button"
                onClick={submitNewOrg}
                className="w-full px-4 py-2 rounded-full bg-[#1E3A8A] text-white text-sm font-medium"
                data-testid="ref-new-org-save"
              >
                Add to Resources & use here
              </button>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-[#334155] mb-2">Contact info</label>
            <input value={form.contact_info} onChange={(e) => setForm({ ...form, contact_info: e.target.value })} className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0]" placeholder="Phone, email, or address" data-testid="ref-contact-info" />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#334155] mb-2">Notes</label>
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="w-full px-4 py-3 rounded-xl bg-white border border-[#E5E5E0]" />
          </div>
          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="flex-1 py-3 rounded-full border border-[#E5E5E0] text-[#334155] font-medium">Cancel</button>
            <button type="submit" disabled={submitting} data-testid="ref-submit-btn" className="flex-1 py-3 rounded-full bg-[#1E3A8A] text-white font-medium disabled:opacity-60">
              {submitting ? "Saving…" : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ResourcePicker({ resources, category, value, onPick, onType, onAddNew }) {
  const [open, setOpen] = useState(false);
  const matches = (resources || [])
    .filter((r) => r.active !== false)
    .filter((r) => {
      const q = (value || "").trim().toLowerCase();
      if (!q) return r.category === category || !category;
      return r.name.toLowerCase().includes(q);
    })
    .slice(0, 8);
  return (
    <div className="relative">
      <input
        value={value}
        onChange={(e) => { onType(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        required
        placeholder="Search Resources or type a new organization…"
        className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0]"
        data-testid="ref-org"
      />
      {open && (
        <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-[#E5E5E0] rounded-xl shadow-lg max-h-64 overflow-y-auto" data-testid="ref-org-dropdown">
          {matches.length > 0 ? (
            <ul>
              {matches.map((r) => (
                <li key={r.id}>
                  <button
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); onPick(r); setOpen(false); }}
                    data-testid={`ref-org-pick-${r.id}`}
                    className="w-full text-left px-4 py-2 hover:bg-[#F9F8F6] flex items-start gap-2"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-[#0F172A] truncate">{r.name}</div>
                      <div className="text-[11px] text-[#1E3A8A] truncate">
                        {r.category?.replace(/_/g, " ")}
                        {r.phone ? ` · ${r.phone}` : ""}
                        {r.county ? ` · ${r.county}` : ""}
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <div className="px-4 py-3 text-xs text-[#334155]">No matching Resources.</div>
          )}
          <button
            type="button"
            onMouseDown={(e) => { e.preventDefault(); onAddNew(); setOpen(false); }}
            data-testid="ref-org-add-new"
            className="w-full text-left px-4 py-2 border-t border-[#E5E5E0] hover:bg-[#F9F8F6] text-sm font-medium text-[#1E3A8A]"
          >
            + Add new organization to Resources…
          </button>
        </div>
      )}
    </div>
  );
}
